/**
 * INTERNAL-ONLY price-quote calculator. Reactive scratchpad to quote a prospect
 * by book size + required features on a PMPM/PEPM basis, with % discount and
 * flat upcharges (e.g. dedicated on-prem hosting). No DB writes, no API calls.
 * Must NEVER be linked from or referenced in apps/docs or apps/marketing.
 */
import { formatUsd } from '@commissionsight/shared';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Badge, type Notify, Panel, btn, btnGhost, input, useRun } from '../components/ui.js';
import {
  type AgreementData,
  downloadBlob,
  generateAgreementDocx,
  generateNcaDocx,
  generateNdaDocx,
} from '../lib/agreement.js';
import {
  type FeatureLine,
  type QuoteContact,
  type QuoteInput,
  type QuoteUnit,
  type Upcharge,
  buildQuoteHtml,
  computeQuote,
  defaultFeatureLines,
  quoteSummaryText,
  seedBaseRate,
  unitAbbr,
  unitNoun,
} from '../lib/quote.js';

const num = (s: string): number => {
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};
/** Sub-dollar per-unit rate display (4 decimals). */
const rateUsd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 4 });

export function PriceCalculator({ notify }: { notify: Notify }) {
  const run = useRun(notify);

  const [company, setCompany] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [unit, setUnit] = useState<QuoteUnit>('pmpm');
  const [countStr, setCountStr] = useState('10000');
  const [baseRateStr, setBaseRateStr] = useState(() => seedBaseRate().toString());
  const [rateTouched, setRateTouched] = useState(false);
  const [features, setFeatures] = useState<FeatureLine[]>(defaultFeatureLines);
  const [discountStr, setDiscountStr] = useState('0');
  const [upcharges, setUpcharges] = useState<Upcharge[]>([]);

  // ── SaaS agreement (redline export) fields ──────────────────────────────────
  const todayIso = new Date().toISOString().slice(0, 10);
  const [effIso, setEffIso] = useState(todayIso);
  const [startIso, setStartIso] = useState(todayIso);
  const [termLength, setTermLength] = useState('12 months');
  const [custEntity, setCustEntity] = useState('');
  const [custAddress, setCustAddress] = useState('');
  const [provLegal, setProvLegal] = useState('Plural Software, LLC');
  const [provEntity, setProvEntity] = useState('Delaware limited liability company');
  const [provAddress, setProvAddress] = useState('');

  const count = Math.max(0, Math.floor(num(countStr)));
  const defaultRate = seedBaseRate();

  // Anchor the base rate to the $0.25 default until the operator sets it per plan.
  useEffect(() => {
    if (!rateTouched) setBaseRateStr(defaultRate.toString());
  }, [defaultRate, rateTouched]);

  const quoteInput: QuoteInput = useMemo(
    () => ({
      unit,
      count,
      baseRate: num(baseRateStr),
      features,
      discountPct: num(discountStr),
      upcharges,
    }),
    [unit, count, baseRateStr, features, discountStr, upcharges],
  );
  const result = useMemo(() => computeQuote(quoteInput), [quoteInput]);
  const contact: QuoteContact = { company, contact: contactName, email };

  const abbr = unitAbbr(unit);
  const noun = unitNoun(unit);

  const setFeature = (key: string, patch: Partial<FeatureLine>) =>
    setFeatures((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const copy = () =>
    run(
      async () => {
        await navigator.clipboard.writeText(quoteSummaryText(quoteInput, result, contact));
      },
      { success: 'Quote summary copied to clipboard' },
    );

  // Print just the quote — open a standalone document in its own window so the
  // admin console chrome isn't captured, then trigger the browser print dialog.
  const printQuote = () => {
    const today = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const html = buildQuoteHtml(quoteInput, result, contact, today);
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) {
      notify('Pop-up blocked — allow pop-ups for this site to print the quote.', 'error');
      return;
    }
    w.document.write(html);
    w.document.close();
    w.focus();
    w.addEventListener('load', () => w.print());
    // Fallback if the load event already fired before the listener attached.
    setTimeout(() => w.print(), 400);
  };

  // Merge the master SaaS agreement template with the quote + contact fields into
  // a redline .docx (internal only). Pricing is taken straight from the quote.
  const fmtDate = (iso: string) =>
    iso
      ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '';
  const agreementData = (): AgreementData => {
    const live = upcharges.filter((u) => u.amount > 0);
    const notes = [
      `Annual run-rate ≈ ${formatUsd(result.annualTotal)}.`,
      result.enabledFeatures.length
        ? `Add-ons: ${result.enabledFeatures.map((f) => f.label).join(', ')}.`
        : '',
      num(discountStr) > 0 ? `Discount ${num(discountStr)}%.` : '',
      live.length
        ? `Upcharges: ${live.map((u) => `${u.label || 'Upcharge'} ${formatUsd(u.amount)}/mo`).join(', ')}.`
        : '',
    ]
      .filter(Boolean)
      .join(' ');
    return {
      effectiveDate: fmtDate(effIso),
      providerLegalName: provLegal,
      providerEntity: provEntity,
      providerAddress: provAddress,
      customerLegalName: company,
      customerEntity: custEntity,
      customerAddress: custAddress,
      customerEmail: email,
      billingContact: contactName,
      orderDate: fmtDate(effIso),
      startDate: fmtDate(startIso),
      termLength,
      planService: 'CommissionSight Platform',
      usageMetric: abbr,
      unitRate: `${rateUsd(result.blendedRate)} ${abbr}`,
      includedVolume: `${count.toLocaleString()} ${noun}s`,
      minimumFee: `${formatUsd(result.monthlyTotal)}/mo`,
      priceNotes: notes,
    };
  };
  // Generate one of the agreement docs (SaaS / NDA / NCA), each a separate 1-click
  // download merged from the same quote + contact fields.
  const downloadDoc = (gen: (d: AgreementData) => Promise<Blob>, prefix: string, label: string) => {
    if (!company.trim()) {
      notify('Enter a company name first.', 'error');
      return;
    }
    run(
      async () => {
        const blob = await gen(agreementData());
        const safe = company
          .trim()
          .replace(/[^a-z0-9]+/gi, '-')
          .replace(/^-|-$/g, '');
        downloadBlob(blob, `${prefix}-${safe || 'customer'}.docx`);
      },
      { success: `${label} generated — opening download` },
    );
  };
  const generateAgreement = () =>
    downloadDoc(generateAgreementDocx, 'CommissionSight-SaaS-Agreement', 'SaaS agreement');
  const generateNda = () => downloadDoc(generateNdaDocx, 'CommissionSight-NDA', 'NDA');
  const generateNca = () => downloadDoc(generateNcaDocx, 'CommissionSight-NCA', 'NCA');

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      {/* ── Inputs column ─────────────────────────────────────────── */}
      <div>
        <Panel title="Customer">
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <div className="mb-1 block text-sm font-medium text-neutral-700">Company</div>
              <input
                className={input}
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Insurance Agency"
              />
            </div>
            <div>
              <div className="mb-1 block text-sm font-medium text-neutral-700">Contact</div>
              <input
                className={input}
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>
            <div>
              <div className="mb-1 block text-sm font-medium text-neutral-700">Email</div>
              <input
                className={input}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@acme.com"
              />
            </div>
          </div>
        </Panel>

        <Panel title="Book & base rate">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-1 text-sm font-medium text-neutral-700">Billing unit</div>
              <div className="inline-flex rounded-lg border border-neutral-300 p-0.5">
                {(['pmpm', 'pepm'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => setUnit(u)}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                      unit === u
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'text-neutral-500 hover:text-neutral-900'
                    }`}
                  >
                    {unitAbbr(u)}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-neutral-400">
                {unit === 'pmpm' ? 'Per member per month' : 'Per employee per month'}
              </p>
            </div>

            <div>
              <div className="mb-1 block text-sm font-medium text-neutral-700">
                Book size ({noun}s)
              </div>
              <input
                className={input}
                type="number"
                min={0}
                value={countStr}
                onChange={(e) => setCountStr(e.target.value)}
                placeholder="10000"
              />
            </div>

            <div className="sm:col-span-2">
              <div className="mb-1 block text-sm font-medium text-neutral-700">
                Base rate ({abbr}, $/{noun}/mo)
              </div>
              <div className="flex items-center gap-2">
                <input
                  className={input}
                  type="number"
                  min={0}
                  step="0.01"
                  value={baseRateStr}
                  onChange={(e) => {
                    setRateTouched(true);
                    setBaseRateStr(e.target.value);
                  }}
                />
                {rateTouched && (
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() => {
                      setRateTouched(false);
                      setBaseRateStr(defaultRate.toString());
                    }}
                  >
                    Reset to default
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-neutral-400">
                Default base rate: {rateUsd(defaultRate)} {abbr} (set per feature plan)
                {rateTouched && ' · overridden'}
              </p>
            </div>
          </div>
        </Panel>

        <Panel title="Feature add-ons (incremental PMPM)">
          <p className="mb-3 text-sm text-neutral-500">
            Enabling a feature adds an incremental per-{noun} rate on top of the base.
          </p>
          <div className="divide-y divide-neutral-100">
            {features.map((f) => (
              <div key={f.key} className="flex items-center gap-3 py-2.5">
                <label className="flex flex-1 cursor-pointer items-center gap-2.5">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500/30"
                    checked={f.enabled}
                    onChange={(e) => setFeature(f.key, { enabled: e.target.checked })}
                  />
                  <span className="text-sm text-neutral-800">{f.label}</span>
                  {f.roadmap && <Badge tone="amber">roadmap</Badge>}
                </label>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-neutral-400">+$</span>
                  <input
                    className={`${input} w-24 text-right`}
                    type="number"
                    min={0}
                    step="0.01"
                    disabled={!f.enabled}
                    value={f.addOnRate ? String(f.addOnRate) : ''}
                    placeholder="0.00"
                    onChange={(e) => setFeature(f.key, { addOnRate: num(e.target.value) })}
                  />
                  <span className="w-12 text-xs text-neutral-400">{abbr}</span>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Discount & upcharges">
          <div className="mb-5 max-w-xs">
            <div className="mb-1 block text-sm font-medium text-neutral-700">Discount (%)</div>
            <input
              className={input}
              type="number"
              min={0}
              max={100}
              step="1"
              value={discountStr}
              onChange={(e) => setDiscountStr(e.target.value)}
            />
            <p className="mt-1 text-xs text-neutral-400">
              Applied to the metered software subtotal only.
            </p>
          </div>

          <div className="mb-2 flex items-center justify-between">
            <div className="text-sm font-medium text-neutral-700">Flat upcharges ($/mo)</div>
            <div className="flex gap-2">
              <button
                type="button"
                className={btnGhost}
                onClick={() =>
                  setUpcharges((prev) => [
                    ...prev,
                    { label: 'Dedicated on-prem hosting', amount: 0 },
                  ])
                }
              >
                + On-prem hosting
              </button>
              <button
                type="button"
                className={btnGhost}
                onClick={() => setUpcharges((prev) => [...prev, { label: '', amount: 0 }])}
              >
                + Upcharge
              </button>
            </div>
          </div>
          {upcharges.length === 0 ? (
            <p className="text-sm text-neutral-400">
              No upcharges. Add dedicated hosting, onboarding, etc.
            </p>
          ) : (
            <div className="space-y-2">
              {upcharges.map((u, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional, no stable id.
                <div key={i} className="flex items-center gap-2">
                  <input
                    className={`${input} flex-1`}
                    type="text"
                    value={u.label}
                    placeholder="Label (e.g. Dedicated on-prem hosting)"
                    onChange={(e) =>
                      setUpcharges((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                      )
                    }
                  />
                  <span className="text-xs text-neutral-400">$</span>
                  <input
                    className={`${input} w-28 text-right`}
                    type="number"
                    min={0}
                    step="1"
                    value={u.amount ? String(u.amount) : ''}
                    placeholder="0"
                    onChange={(e) =>
                      setUpcharges((prev) =>
                        prev.map((x, j) => (j === i ? { ...x, amount: num(e.target.value) } : x)),
                      )
                    }
                  />
                  <button
                    type="button"
                    className="rounded-lg px-2 py-1 text-sm text-neutral-400 transition hover:bg-rose-50 hover:text-rose-600"
                    onClick={() => setUpcharges((prev) => prev.filter((_, j) => j !== i))}
                    aria-label="Remove upcharge"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="SaaS agreement (redline export)">
          <p className="mb-3 text-sm text-neutral-500">
            Merges the master SaaS agreement with the pricing above + the fields below into a
            redline
            <span className="font-medium"> .docx</span>. Company, contact, and email come from the
            Customer panel; pricing from the quote. Internal use only.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <AgField label="Effective / order date">
              <input
                className={input}
                type="date"
                value={effIso}
                onChange={(e) => setEffIso(e.target.value)}
              />
            </AgField>
            <AgField label="Subscription start date">
              <input
                className={input}
                type="date"
                value={startIso}
                onChange={(e) => setStartIso(e.target.value)}
              />
            </AgField>
            <AgField label="Initial term">
              <input
                className={input}
                type="text"
                value={termLength}
                onChange={(e) => setTermLength(e.target.value)}
                placeholder="12 months"
              />
            </AgField>
            <AgField label="Customer entity / state">
              <input
                className={input}
                value={custEntity}
                onChange={(e) => setCustEntity(e.target.value)}
                placeholder="a Wisconsin corporation"
              />
            </AgField>
            <div className="sm:col-span-2">
              <AgField label="Customer principal address">
                <input
                  className={input}
                  value={custAddress}
                  onChange={(e) => setCustAddress(e.target.value)}
                  placeholder="500 Oak Ave, Madison, WI 53703"
                />
              </AgField>
            </div>
            <AgField label="Provider legal name">
              <input
                className={input}
                value={provLegal}
                onChange={(e) => setProvLegal(e.target.value)}
              />
            </AgField>
            <AgField label="Provider entity / state">
              <input
                className={input}
                value={provEntity}
                onChange={(e) => setProvEntity(e.target.value)}
              />
            </AgField>
            <div className="sm:col-span-2">
              <AgField label="Provider address">
                <input
                  className={input}
                  value={provAddress}
                  onChange={(e) => setProvAddress(e.target.value)}
                />
              </AgField>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={btn} onClick={generateAgreement}>
              Master SaaS (.docx)
            </button>
            <button type="button" className={btnGhost} onClick={generateNda}>
              NDA (.docx)
            </button>
            <button type="button" className={btnGhost} onClick={generateNca}>
              NCA (.docx)
            </button>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Three separate downloads, each merged from the fields above: the Master SaaS Agreement
            (with pricing), the Mutual NDA &amp; Data Protection Agreement, and the
            Non-Circumvention &amp; Non-Solicitation Agreement. Provider details and NJ venue are
            fixed in the masters.
          </p>
        </Panel>
      </div>

      {/* ── Summary column (sticky) ───────────────────────────────── */}
      <div className="lg:sticky lg:top-24 lg:self-start">
        <section className="rounded-2xl border border-neutral-200/80 bg-white p-5 shadow-sm ring-1 ring-black/[0.02]">
          <h2 className="mb-4 font-semibold">Quote</h2>

          <div className="rounded-xl bg-emerald-50 p-4 ring-1 ring-emerald-100">
            <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">
              Monthly total
            </div>
            <div className="mt-0.5 text-3xl font-semibold tabular-nums tracking-tight text-emerald-700">
              {formatUsd(result.monthlyTotal)}
            </div>
            <div className="mt-0.5 text-xs text-emerald-600/80">
              {rateUsd(result.blendedRate)} {abbr} blended · {count.toLocaleString()} {noun}s
            </div>
          </div>

          <dl className="mt-4 space-y-2 text-sm">
            <Row label={`Effective rate (${abbr})`} value={`${rateUsd(result.effectiveRate)}`} />
            <Row label="Metered subtotal" value={`${formatUsd(result.meteredMonthly)}/mo`} />
            {num(discountStr) > 0 && (
              <Row
                label={`Discount (${num(discountStr)}%)`}
                value={`-${formatUsd(result.discountAmount)}/mo`}
                tone="rose"
              />
            )}
            {result.upchargesTotal > 0 && (
              <Row label="Upcharges" value={`+${formatUsd(result.upchargesTotal)}/mo`} />
            )}
            <div className="my-2 border-neutral-100 border-t" />
            <Row label="Annual total" value={formatUsd(result.annualTotal)} hint="run-rate est." />
          </dl>

          {result.enabledFeatures.length > 0 && (
            <div className="mt-4 rounded-lg bg-neutral-50 p-3">
              <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-neutral-500">
                Feature add-ons
              </div>
              <ul className="space-y-1 text-sm">
                {result.enabledFeatures.map((f) => (
                  <li key={f.key} className="flex justify-between gap-2">
                    <span className="truncate text-neutral-600">{f.label}</span>
                    <span className="shrink-0 tabular-nums text-neutral-800">
                      +{rateUsd(f.addOnRate)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 flex gap-2">
            <button type="button" className={`${btn} flex-1`} onClick={copy}>
              Copy quote summary
            </button>
            <button type="button" className={btnGhost} onClick={printQuote}>
              Print
            </button>
          </div>
          <p className="mt-3 text-center text-xs text-neutral-400">
            Internal estimate — not published.
          </p>
        </section>
      </div>
    </div>
  );
}

function AgField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 block text-sm font-medium text-neutral-700">{label}</div>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  hint,
  tone = 'neutral',
}: { label: string; value: string; hint?: string; tone?: 'neutral' | 'rose' }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-neutral-500">
        {label}
        {hint && <span className="ml-1.5 text-xs text-neutral-400">· {hint}</span>}
      </dt>
      <dd className={`tabular-nums ${tone === 'rose' ? 'text-rose-600' : 'text-neutral-900'}`}>
        {value}
      </dd>
    </div>
  );
}
